/**
 * #3312 Sheet 복사(JC): 화면 표를 파일 다운로드 없이 클립보드로 복사해 고객의 매크로 Excel 에 붙여넣는다.
 *
 * 헤더 정의는 기존 Excel 추출(export-xlsx)과 같은 { key, name, dataType, format? } 형식을 그대로 받는다.
 * - key 가 빈 문자열이면 formatter·숫자 판정보다 먼저 빈칸으로 둔다(원가 권한 마스킹, 선행 빈 열). 열 위치는 유지된다.
 * - dataType String(및 미지정) 열은 값이 숫자여도 텍스트 셀이다. HTML 형식에 mso-number-format 을 붙여
 *   Excel 이 앞자리 0·14자리 바코드·"2025/9" 같은 값을 숫자·날짜로 바꾸지 않게 한다.
 * - dataType Number/Currency/Percentage 열은 실제 숫자만 숫자 셀로 두고, 문자열은 변환하지 않고 그대로 낸다.
 * - dataType Date 열은 서식을 강제하지 않아 Excel 이 날짜로 읽는다.
 * 클립보드에는 text/html 과 text/plain(TSV) 을 함께 넣는다. Windows Excel 은 HTML 형식을 우선 사용한다.
 * - options.total_row(3차): 헤더 바로 아래 첫 행에 들어가는 합계 행 객체. 헤더 key 로 값을 읽되 formatter 는 적용하지 않아
 *   합계 객체에 없는 상태·날짜 열이 채워지지 않는다(라벨·지정 숫자 외 셀은 공란).
 *
 * 이 파일은 프로젝트 모듈에 의존하지 않는 순수 로직만 담는다(단위 테스트 대상).
 */

const NUMERIC_TYPES = ['Number', 'Currency', 'Percentage'];
const DATE_TYPES = ['Date'];

/** Excel 이 셀을 텍스트로 읽게 하는 HTML 셀 style. */
export const TEXT_NUMBER_FORMAT = "mso-number-format:'\\@'";

/** null/undefined 를 빈 문자열로 바꾼다. 화면 템플릿의 {{ a }}/{{ b }} 와 같은 출력이 필요할 때 쓴다. */
export function textOrEmpty(value) {
	return value === null || value === undefined ? '' : String(value);
}

/** 셀 하나가 한 줄로 유지되게 탭·개행을 공백으로 바꾼다. TSV 의 열·행 구분자와 충돌하지 않게 한다. */
export function flattenText(value) {
	return textOrEmpty(value).replace(/[\t\r\n]+/g, ' ');
}

export function escapeHtml(value) {
	return textOrEmpty(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function blankCell() {
	return { kind: 'blank', value: '' };
}

/** 헤더 정의 하나와 행 하나로 셀을 만든다. kind: blank | text | number | general */
export function sheetCell(header, row) {
	if (!header || !header.key) return blankCell();
	const raw = typeof header.format === 'function' ? header.format(row) : row ? row[header.key] : undefined;
	if (raw === null || raw === undefined || raw === '') return blankCell();
	if (NUMERIC_TYPES.includes(header.dataType)) {
		if (typeof raw === 'number') return Number.isFinite(raw) ? { kind: 'number', value: raw } : blankCell();
		return { kind: 'general', value: flattenText(raw) };
	}
	if (DATE_TYPES.includes(header.dataType)) return { kind: 'general', value: flattenText(raw) };
	return { kind: 'text', value: flattenText(raw) };
}

/**
 * 유한 숫자만 합산해 digits 자리로 반올림한다(절대값 기준 반올림이라 -3.345 는 -3.35). 문자열·null·NaN 은 무시하고
 * 값이 없으면 0 이다. 부동소수 합(0.1+0.2, 1.005*100 = 100.4999…)이 .5 바로 아래로 떨어지는 오차는 (1 + EPSILON) 보정으로 흡수한다.
 */
export function sumColumn(rows, key, digits = 2) {
	let sum = 0;
	(Array.isArray(rows) ? rows : []).forEach(row => {
		const value = row ? row[key] : undefined;
		if (typeof value === 'number' && Number.isFinite(value)) sum += value;
	});
	if (sum === 0) return 0;
	const factor = Math.pow(10, digits);
	const rounded = Math.round(Math.abs(sum) * factor * (1 + Number.EPSILON)) / factor;
	return sum < 0 ? -rounded : rounded;
}

/**
 * 헤더 행 + 데이터 행 격자. options.leading_blank 면 헤더·값이 모두 빈 첫 열을 추가하고,
 * options.total_row 가 있으면 그 객체를 첫 데이터 행(formatter 미적용)으로 넣고 total_rows 에 1 을 남긴다.
 */
export function buildSheetGrid(headers, rows, options) {
	const leading = options && options.leading_blank ? [{ key: '', name: '' }] : [];
	const columns = leading.concat(Array.isArray(headers) ? headers : []);
	const total_columns = columns.map(column => Object.assign({}, column, { format: undefined }));
	const total_row = options && options.total_row ? [options.total_row] : [];
	const data_rows = Array.isArray(rows) ? rows : [];
	return {
		header: columns.map(column => flattenText(column.name)),
		rows: total_row.map(row => total_columns.map(column => sheetCell(column, row))).concat(data_rows.map(row => columns.map(column => sheetCell(column, row)))),
		total_rows: total_row.length,
	};
}

/** 헤더 행이 비어 있으면(부분 선택 블록) 데이터 행만 낸다. */
export function toTsv(grid) {
	const header_lines = grid.header.length ? [grid.header] : [];
	const lines = header_lines.concat(grid.rows.map(row => row.map(cell => (cell.kind === 'number' ? String(cell.value) : cell.value))));
	return lines.map(cells => cells.join('\t')).join('\r\n');
}

function htmlCell(cell) {
	if (cell.kind === 'number') return '<td>' + String(cell.value) + '</td>';
	if (cell.kind === 'text') return '<td style="' + TEXT_NUMBER_FORMAT + '">' + escapeHtml(cell.value) + '</td>';
	return '<td>' + escapeHtml(cell.value) + '</td>';
}

export function toHtml(grid) {
	const header = grid.header.length ? '<tr>' + grid.header.map(name => '<td style="' + TEXT_NUMBER_FORMAT + '">' + escapeHtml(name) + '</td>').join('') + '</tr>' : '';
	const body = grid.rows.map(row => '<tr>' + row.map(htmlCell).join('') + '</tr>').join('');
	return '<table>' + header + body + '</table>';
}

/**
 * execCommand('copy') 폴백. copy 이벤트 동안만 listener 를 붙여 두 형식을 넣고 preventDefault 한다.
 * 임시 textarea·listener·선택 영역·포커스는 finally 에서 되돌려 표의 부분 선택 Ctrl+C 에 영향을 주지 않는다.
 * 반환: 'fallback'(두 형식) | 'fallback-text'(이벤트에 clipboardData 가 없어 TSV 만) | null(실패)
 */
function copyByCommand(doc, html, text) {
	const listener = event => {
		if (!event.clipboardData) return;
		event.clipboardData.setData('text/html', html);
		event.clipboardData.setData('text/plain', text);
		event.preventDefault();
		listener.handled = true;
	};
	listener.handled = false;
	const previous_focus = doc.activeElement;
	const selection = typeof doc.getSelection === 'function' ? doc.getSelection() : null;
	const saved_ranges = [];
	if (selection) for (let i = 0; i < selection.rangeCount; i++) saved_ranges.push(selection.getRangeAt(i));
	const holder = doc.createElement('textarea');
	holder.value = text;
	holder.setAttribute('readonly', '');
	holder.style.position = 'fixed';
	holder.style.top = '0';
	holder.style.left = '-9999px';
	holder.style.opacity = '0';
	doc.body.appendChild(holder);
	doc.addEventListener('copy', listener, true);
	let executed = false;
	try {
		holder.focus();
		holder.select();
		executed = Boolean(doc.execCommand('copy'));
	} catch (error) {
		executed = false;
	} finally {
		doc.removeEventListener('copy', listener, true);
		if (holder.parentNode) holder.parentNode.removeChild(holder);
		if (selection) {
			selection.removeAllRanges();
			saved_ranges.forEach(range => selection.addRange(range));
		}
		if (previous_focus && typeof previous_focus.focus === 'function') previous_focus.focus();
	}
	if (!executed) return null;
	return listener.handled ? 'fallback' : 'fallback-text';
}

function pick(deps, name, fallback) {
	return deps && deps[name] !== undefined ? deps[name] : fallback;
}

/**
 * 격자를 클립보드에 넣는다. Async Clipboard(text/html + text/plain) 를 먼저 쓰고, 미지원·거부면 execCommand 폴백을 쓴다.
 * 성공: { method: 'async' | 'fallback' | 'fallback-text', rows }, 실패: reject.
 * deps 는 테스트용 주입(navigator, document, ClipboardItem, Blob).
 */
export async function copySheetToClipboard(grid, deps) {
	const html = toHtml(grid);
	const text = toTsv(grid);
	const scope = typeof window !== 'undefined' ? window : {};
	const nav = pick(deps, 'navigator', scope.navigator);
	const doc = pick(deps, 'document', scope.document);
	const ClipboardItemCtor = pick(deps, 'ClipboardItem', scope.ClipboardItem);
	const BlobCtor = pick(deps, 'Blob', scope.Blob);
	let async_error = null;
	if (nav && nav.clipboard && typeof nav.clipboard.write === 'function' && ClipboardItemCtor && BlobCtor) {
		try {
			const item = new ClipboardItemCtor({
				'text/html': new BlobCtor([html], { type: 'text/html' }),
				'text/plain': new BlobCtor([text], { type: 'text/plain' }),
			});
			await nav.clipboard.write([item]);
			return { method: 'async', rows: grid.rows.length };
		} catch (error) {
			async_error = error;
		}
	}
	if (doc && doc.body && typeof doc.execCommand === 'function') {
		const method = copyByCommand(doc, html, text);
		if (method) return { method, rows: grid.rows.length };
	}
	throw async_error || new Error('Clipboard copy is not available in this browser.');
}
